import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CurrencyService } from '../../../core/services/currency.service';
import { TranslateService } from '@ngx-translate/core';
import { Budget, BudgetCustomItem, BudgetItem, BudgetProduct, BudgetServiceItem } from '../interfaces/budget.interface';
import * as pdfMake from 'pdfmake/build/pdfmake';
import 'pdfmake/build/vfs_fonts';
import { PdfFontLoaderService } from 'src/app/services/pdf-font-loader.service';
import { isColorBrigth } from 'src/app/core/utils/utils';
import { CenterGroupStyle, User, UserRoleService } from 'src/app/core/services/role.service';
import { TDocumentDefinitions, TableCell } from 'pdfmake/interfaces';
import { CenterInfo } from 'src/app/core/interfaces/my-workshop';
import { MyAccountService } from 'src/app/core/services/my-account.service';
import { BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { BudgetPdfPreviewModalComponent } from '../components/budget-pdf-preview-modal/budget-pdf-preview-modal.component';
import { composeWithSymbol, resolveLocale } from 'src/app/core/utils/currency.utils';
import { TimezoneService } from 'src/app/core/utils/timezone.service';

type LogoAsset =
    | { kind: 'raster'; dataUrl: string }
    | { kind: 'svg'; markup: string };

@Injectable({
    providedIn: 'root'
})
export class BudgetDocumentService implements OnDestroy {
    private readonly apiUrl = environment.cafler_backoffice_back_url;
    centerInfo: CenterInfo | undefined;

    private readonly destroy$ = new Subject<void>();

    constructor(
        private readonly http: HttpClient,
        private readonly currencyService: CurrencyService,
        private readonly translate: TranslateService,
        private readonly UserRoleService: UserRoleService,
        private readonly myAccountService: MyAccountService,
        private readonly bsModalService: BsModalService,
        private readonly pdfFontLoader: PdfFontLoaderService,
        private readonly timezoneService: TimezoneService,
    ) {
        this.loadCenterCountry();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private loadCenterCountry(): void {
        // takeUntil para garantizar que la suscripción se cierra si el servicio se
        // destruye (en lazy modules), evitando suscripciones huérfanas.
        this.myAccountService.getCenterInfo()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (centerInfo: CenterInfo) => {
                    this.centerInfo = centerInfo;
                },
                error: (error: unknown) => {
                    console.error('Error loading center info:', error);
                }
            });
    }

    /**
     * Convierte símbolos de moneda especiales a una representación segura para PDF
     * Los caracteres especiales se convierten a códigos de moneda de 3 letras
     * para evitar problemas de codificación en pdfMake.
     * Nota: CRC (Costa Rica) ahora usa símbolo $ como USD.
     */
    private getPDFSafeCurrency(currencySymbol: string): string {
        const currencyMap: { [key: string]: string } = {
            '€': '€',           // Euro - soportado
            '$': '$',           // Dólar (incluye USD y CRC) - soportado
            '£': '£',           // Libra - soportado
            'CLP': 'CLP',       // Peso chileno - código
            '₨': 'INR',         // Rupia india - código
            '₩': 'KRW',         // Won coreano - código
            '₪': 'ILS',         // Shekel israelí - código
            '₫': 'VND',         // Dong vietnamita - código
            '₱': 'MXN',         // Peso mexicano - código
            '₹': 'INR',         // Rupia india - código
            '₺': 'TRY',         // Lira turca - código
            '₴': 'UAH',         // Grivna ucraniana - código
            '₸': 'KZT',         // Tenge kazajo - código
            '₼': 'AZN',         // Manat azerbaiyano - código
            '₽': 'RUB',         // Rublo ruso - código
            '₾': 'GEL',         // Lari georgiano - código
            '¥': '¥',           // Yuan/Yen - soportado
            '₣': 'CHF',         // Franco suizo - código
            '₥': 'MLF',         // Franco maliense - código
            'ƒ': 'ANG'          // Florín de las Antillas Holandesas - código
        };

        return currencyMap[currencySymbol] || currencySymbol;
    }

    private formatPdfCurrency(
        amount: number | null | undefined,
        currencyCode: string,
        locale: string,
        safeSymbol: string,
        minimumFractionDigits = 2,
        maximumFractionDigits = 2
    ): string {
        if (amount === null || amount === undefined) return '-';
        const numeric = Number(amount);
        if (!Number.isFinite(numeric)) return '-';
        const formatted = composeWithSymbol(
            numeric,
            safeSymbol,
            currencyCode,
            locale,
            minimumFractionDigits,
            maximumFractionDigits
        );
        return formatted.replace(/\u00A0/g, ' ');
    }

    private async fetchLogoForPdf(url: string): Promise<LogoAsset | null> {
        if (!url) return null;
        try {
            const blob = await firstValueFrom(this.http.get(url, { responseType: 'blob' }));
            if (!blob) return null;

            if (this.isSvgBlob(blob, url)) {
                const markup = (await blob.text()).trim();
                return markup ? { kind: 'svg', markup } : null;
            }

            const dataUrl = await this.blobToDataUrl(blob);
            return dataUrl ? { kind: 'raster', dataUrl } : null;
        } catch (error) {
            console.error('Error loading logo image for PDF:', error);
            return null;
        }
    }

    private isSvgBlob(blob: Blob, url: string): boolean {
        const mime = (blob.type || '').toLowerCase();
        if (mime.includes('svg')) return true;
        return /\.svg(?:[?#]|$)/i.test(url);
    }

    private blobToDataUrl(blob: Blob): Promise<string | null> {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const result = reader.result;
                resolve(typeof result === 'string' ? result : null);
            };
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(blob);
        });
    }

    async getPDFtoSend(budget: Budget, validUntil: Date | null, centerInfo: CenterInfo): Promise<string> {
        const TDocumentDefinitions = await this.generatePdf(budget, validUntil, centerInfo);
        return new Promise((resolve) => {
            pdfMake.createPdf(TDocumentDefinitions).getBase64((data: string) => {
                // `data` es el PDF en base64
                resolve(data);
            });
        });
    }

    async downloadPDFOrShow(budget: Budget, validUntil: Date | null, centerInfo: CenterInfo | null): Promise<void> {
        if (this.isWebView) {
            this.showBudgetPDF(budget, validUntil, centerInfo)
        } else {
            this.downloadPDF(budget, validUntil, centerInfo)
        }
    }

    async downloadPDF(budget: Budget, validUntil: Date | null, centerInfo: CenterInfo | null): Promise<void> {
        const centerInfoUse = centerInfo ?? this.centerInfo;
        let TDocumentDefinitions = await this.generatePdf(budget, validUntil, centerInfoUse);
        pdfMake.createPdf(TDocumentDefinitions).download(`${this.translate.instant('pages.budget.pdf.budget')}_${budget.id}.pdf`)
    }

    async showBudgetPDF(budget: Budget, validUntil: Date | null, centerInfo: CenterInfo | null): Promise<void> {
        const centerInfoUse = centerInfo ?? this.centerInfo;
        const base64 = await this.getPDFtoSend(budget, validUntil, centerInfoUse!);

        const config: ModalOptions = {
            class: "modal-80vw-width modal-dialog-centered-custom",
            initialState: {
                base64,
                budgetId: budget.id,
                download: () => this.downloadPdfFromBase64(base64, `budget_${budget.id}.pdf`),
            }
        };

        this.bsModalService.show(BudgetPdfPreviewModalComponent, config);
    }


    get isWebView(): boolean {
        const ua = navigator.userAgent;
        return /wv|WebView|ReactNative/i.test(ua) || (ua.includes('Version/') && ua.includes('Mobile'));
    }



    async generatePdf(documentData: Budget, validUntil: Date | null, centerInfo: CenterInfo | undefined): Promise<TDocumentDefinitions> {
        await this.pdfFontLoader.ensureFontsLoaded();

        // BRANDING SETTING
        let user: User | null = await firstValueFrom(this.UserRoleService.getCurrentUser());
        let CenterGroupStyle: CenterGroupStyle | null = null;;
        if (user?.centerGroupStyle) {
            CenterGroupStyle = user.centerGroupStyle;
        }
        const principalBrandingColor = CenterGroupStyle?.themePrimary ?? "#007BFF"
        const secundaryBrandingColor = CenterGroupStyle?.themeSecondary ?? "#007BFF"
        // fetchLogoForPdf captura sus errores y devuelve null; aquí lo respetamos
        // al construir el PDF (si no hay logo, el PDF se genera igual sin imagen).
        const logoAsset: LogoAsset | null = CenterGroupStyle?.image
            ? await this.fetchLogoForPdf(CenterGroupStyle.image)
            : null;
        // Obtener moneda y preparar formateo seguro para PDF
        const lang = this.translate.currentLang || this.translate.getDefaultLang() || 'en';
        const currencyCode = this.currencyService.getCurrentCurrencyCode();
        const rawCurrencySymbol = this.currencyService.getCurrentCurrencySymbol();
        const safeSymbol = this.getPDFSafeCurrency(rawCurrencySymbol);
        const locale = resolveLocale(undefined, lang);
        const formatMoney = (value: number | null | undefined, min = 2, max = 2) =>
            this.formatPdfCurrency(value, currencyCode, locale, safeSymbol, min, max);

        const BudgetProduct = [...documentData.budgetProducts]

        // HEADER DOC TABLE

        const leftColumn: any[] = [];
        const invoiceTitle = {
            table: {
                body: [
                    [
                        {
                            text: this.translate.instant('pages.budget.pdf.budget'),
                            color: isColorBrigth(principalBrandingColor) ? 'black' : 'white',
                            fillColor: principalBrandingColor,
                            alignment: 'center',
                            margin: [40, 5, 40, 5], // padding interno (L, T, R, B)
                            fontSize: 16,
                            bold: true,
                            border: [false, false, false, false]
                        }
                    ]
                ]
            },
            layout: 'noBorders',
            margin: [0, 2, 0, 0]
        }

        if (logoAsset) {
            leftColumn.push(
                logoAsset.kind === 'svg'
                    ? { svg: logoAsset.markup, width: 150 }
                    : { image: logoAsset.dataUrl, width: 150 }
            );
        }
        leftColumn.push(invoiceTitle);

        // CENTER DATA

        const centerData = [
            { text: centerInfo?.name!, style: 'header' },
            { text: centerInfo?.address!, style: 'simple_text_data' },
            { text: `${this.translate.instant('pages.budget.pdf.email')}: ${centerInfo?.email!}`, style: 'simple_text_data' },
            { text: `${this.translate.instant('pages.budget.pdf.cifNif')}: ${centerInfo?.fiscalAddress!}`, style: 'simple_text_data' },
        ]

        // FORMAT DATE

        const country = centerInfo?.country || 'ES';
        const formatDate = (date: Date | null) => {
            if (!date) { return "" }
            return this.timezoneService.formatDateForCenter(new Date(date), country, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: undefined,
                minute: undefined,
                second: undefined,
            });
        };

        // iTEM TABLE BORDERS

        const borderTableItem = [true, false, true, false]
        const borderTableLastItem = [true, false, true, true]

        // PDF DOCUMENT

        const docDefinition: TDocumentDefinitions = {
            header: (currentPage: number) => {
                if (currentPage === 1) {
                    return {
                        table: {
                            widths: ['*', 200],
                            body: [
                                [
                                    { text: '', border: [false, false, false, false] },
                                    {
                                        text: '',
                                        fillColor: secundaryBrandingColor ?? '#007BFF',
                                        fontSize: 22,
                                        margin: [0, 20, 0, 20],
                                        border: [false, false, false, false]
                                    }
                                ],
                            ]
                        }
                    };
                }
                return null;
            },
            content: [
                // HEADER: LOGO AND COMPANY DATA
                {
                    columns: [
                        [
                            leftColumn,
                        ],
                    ]
                },
                {
                    columns: [
                        centerData,
                        [
                            { text: `${this.translate.instant('pages.budget.pdf.budgetNumber')}: ${documentData.id}`, style: "simple_text_data", alignment: 'right' },
                            { text: `${this.translate.instant('pages.budget.pdf.date')}: ${formatDate(new Date())}`, style: "simple_text_data", alignment: 'right' },
                            { text: `${this.translate.instant('pages.budget.pdf.validUntil')}: ${formatDate(validUntil)}`, style: "simple_text_data", alignment: 'right' }
                        ]
                    ],
                    margin: [0, 10, 0, 0]
                },


                // CLIENT & VEHICLE DATA TABLE

                {
                    stack: [
                        {
                            canvas: [
                                {
                                    type: 'rect',
                                    x: 0, y: 0,
                                    w: 520, h: 80, // ajusta al tamaño de la tabla
                                    r: 10, // <-- radio
                                    lineColor: 'black',
                                    lineWidth: 1
                                }
                            ]
                        },
                        {
                            table: {
                                widths: ['*', '*'],
                                body: [
                                    [
                                        { text: `${this.translate.instant('pages.budget.pdf.clientNumber')}: ${documentData.client?.id ?? 'N/A'}`, border: [false, false, false, false] as [boolean, boolean, boolean, boolean], style: "dataText_blue" },
                                        { text: `${this.translate.instant('pages.budget.pdf.BRAND')}: ${documentData.vehicle?.brand ?? 'N/A'}`, border: [false, false, false, false] as [boolean, boolean, boolean, boolean], style: "dataText_blue" }
                                    ],
                                    [
                                        { text: `${this.translate.instant('pages.budget.pdf.FULL_NAME')}: ${documentData.client?.fullName}`, border: [false, false, false, false] as [boolean, boolean, boolean, boolean], style: "dataText_blue" },
                                        { text: `${this.translate.instant('pages.budget.pdf.MODEL')}: ${documentData.vehicle?.model ?? 'N/A'}`, border: [false, false, false, false] as [boolean, boolean, boolean, boolean], style: "dataText_blue" }
                                    ],
                                    [
                                        { text: `${this.translate.instant('pages.budget.pdf.email')}: ${documentData.client?.email}`, border: [false, false, false, false] as [boolean, boolean, boolean, boolean], style: "dataText_blue" },
                                        { text: `${this.translate.instant('pages.budget.pdf.LICENSE_PLATE')}: ${documentData.vehicle?.licensePlate ?? 'N/A'}`, border: [false, false, false, false] as [boolean, boolean, boolean, boolean], style: "dataText_blue" }

                                    ],
                                    [
                                        { text: `${this.translate.instant('pages.budget.pdf.ADDRESS')}: ${documentData.client?.address ?? 'N/A'}`, border: [false, false, false, false] as [boolean, boolean, boolean, boolean], style: "dataText_blue" },
                                        { text: `${this.translate.instant('pages.budget.pdf.vin')}: ${documentData.vehicle?.vin ?? 'N/A'}`, border: [false, false, false, false] as [boolean, boolean, boolean, boolean], style: "dataText_blue" }

                                    ],
                                    [
                                        { text: `${this.translate.instant('pages.budget.pdf.PHONE_NUMBER')}: ${documentData.client?.phoneNumber}`, border: [false, false, false, false] as [boolean, boolean, boolean, boolean], style: "dataText_blue" },
                                        { text: '', border: [false, false, false, false] as [boolean, boolean, boolean, boolean], style: "dataText_blue" },
                                    ],

                                ]
                            },
                            layout: {
                                hLineWidth: () => 1,
                                vLineWidth: () => 1,
                                hLineColor: () => 'black',
                                vLineColor: () => 'black',
                                paddingLeft: () => 0,
                                paddingRight: () => 0,
                                paddingTop: () => 0,
                                paddingBottom: () => 0
                            },
                            margin: [10, -70, 10, 0]
                        }
                    ],
                    margin: [0, 10, 0, 0]
                },


                // PRODUCT & ITEMS & SERVICE & CUSTOM_ITEMS TABLE
                {
                    table: {
                        widths: ['*', 40, 86, 106],
                        body: [
                            [
                                // { text: `${this.translate.instant('pages.budget.pdf.ITEM_#')}`, style: 'tableHeader' },
                                { text: `${this.translate.instant('pages.budget.pdf.DESCRIPTION')}`, style: 'tableHeader' } as TableCell,
                                { text: `${this.translate.instant('pages.budget.pdf.QUANTITY')}`, style: 'tableHeader' } as TableCell,
                                { text: `${this.translate.instant('pages.budget.pdf.UNIT_PRICE')}`, style: 'tableHeader' } as TableCell,
                                { text: `${this.translate.instant('pages.budget.pdf.TOTAL_PRICE')}`, style: 'tableHeader' } as TableCell
                            ],
                            ...BudgetProduct.flatMap((i: BudgetProduct, index: number) => {
                                const border = borderTableLastItem as [boolean, boolean, boolean, boolean];
                                const productCode = (i.productCode ?? '').trim();
                                const productBrand = (i.brand ?? '').trim();
                                const productModel = (i.model ?? i.productName ?? '').trim();
                                const productIdentifier = (i.identifier ?? '').trim();
                                const descriptionLine = [productCode, productBrand, productModel]
                                    .filter((part) => part.length > 0)
                                    .join(' ');
                                const fullDescription = [descriptionLine, productIdentifier]
                                    .filter((part) => part.length > 0)
                                    .join('\n');

                                return [
                                    // Product Row
                                    [
                                        { text: fullDescription, style: 'tableCell', border } as TableCell,
                                        { text: i.quantity.toString(), style: 'tableCell', border, alignment: 'center' } as TableCell,
                                        { text: formatMoney(i.price), style: 'tableCell', border, alignment: 'center' } as TableCell,
                                        { text: formatMoney((i.price ?? 0) * i.quantity), style: 'tableCell', border, alignment: 'center' } as TableCell
                                    ],
                                    // Extra row: Ecotax
                                    [
                                        { text: this.translate.instant('pages.budget.pdf.ecoTax'), style: 'tableCell', border } as TableCell,
                                        { text: i.quantity.toString(), style: 'tableCell', border, alignment: 'center' } as TableCell,
                                        { text: formatMoney(i.ecoTaxAmount ?? 0), style: 'tableCell', border, alignment: 'center' } as TableCell,
                                        { text: formatMoney((i.ecoTaxAmount ?? 0) * i.quantity), style: 'tableCell', border, alignment: 'center' } as TableCell
                                    ],
                                ];
                            }),
                            // Items row
                            ...documentData.budgetItems.map((i: BudgetItem, index: number) => [
                                { text: i.name, style: 'tableCell', border: ((index == (documentData.budgetItems?.length - 1)) ? borderTableLastItem : borderTableItem) as [boolean, boolean, boolean, boolean] } as TableCell,
                                { text: i.quantity.toString(), style: 'tableCell', border: ((index == (documentData.budgetItems?.length - 1)) ? borderTableLastItem : borderTableItem) as [boolean, boolean, boolean, boolean], alignment: 'center' } as TableCell,
                                { text: formatMoney(i.price), style: 'tableCell', border: ((index == (documentData.budgetItems?.length - 1)) ? borderTableLastItem : borderTableItem) as [boolean, boolean, boolean, boolean], alignment: 'center' } as TableCell,
                                { text: formatMoney((i.price ?? 0) * i.quantity), border: ((index == (documentData.budgetItems?.length - 1)) ? borderTableLastItem : borderTableItem) as [boolean, boolean, boolean, boolean], style: 'tableCell', alignment: 'center' } as TableCell
                            ]),

                            // Services row
                            ...documentData.budgetServices.map((i: BudgetServiceItem, index: number) => [
                                { text: i.serviceName, style: 'tableCell', border: ((index == (documentData.budgetServices?.length - 1)) ? borderTableLastItem : borderTableItem) as [boolean, boolean, boolean, boolean] } as TableCell,
                                { text: i.quantity.toString(), style: 'tableCell', border: ((index == (documentData.budgetServices?.length - 1)) ? borderTableLastItem : borderTableItem) as [boolean, boolean, boolean, boolean], alignment: 'center' } as TableCell,
                                { text: formatMoney(i.price), style: 'tableCell', border: ((index == (documentData.budgetServices?.length - 1)) ? borderTableLastItem : borderTableItem) as [boolean, boolean, boolean, boolean], alignment: 'center' } as TableCell,
                                { text: formatMoney((i.price ?? 0) * i.quantity), border: ((index == (documentData.budgetServices?.length - 1)) ? borderTableLastItem : borderTableItem) as [boolean, boolean, boolean, boolean], style: 'tableCell', alignment: 'center' } as TableCell
                            ]),

                            // Custom Items row
                            ...documentData.budgetCustomItems.map((i: BudgetCustomItem, index: number) => [
                                { text: i.description, style: 'tableCell', border: ((index == (documentData.budgetCustomItems?.length - 1)) ? borderTableLastItem : borderTableItem) as [boolean, boolean, boolean, boolean] } as TableCell,
                                { text: i.quantity.toString(), style: 'tableCell', border: ((index == (documentData.budgetCustomItems?.length - 1)) ? borderTableLastItem : borderTableItem) as [boolean, boolean, boolean, boolean], alignment: 'center' } as TableCell,
                                { text: formatMoney(i.price), style: 'tableCell', border: ((index == (documentData.budgetCustomItems?.length - 1)) ? borderTableLastItem : borderTableItem) as [boolean, boolean, boolean, boolean], alignment: 'center' } as TableCell,
                                { text: formatMoney((i.price ?? 0) * i.quantity), border: ((index == (documentData.budgetCustomItems?.length - 1)) ? borderTableLastItem : borderTableItem) as [boolean, boolean, boolean, boolean], style: 'tableCell', alignment: 'center' } as TableCell
                            ])
                        ] as TableCell[][]
                    },
                    layout: {
                        hLineWidth: () => 1,
                        vLineWidth: () => 1,
                        hLineColor: () => 'black',
                        vLineColor: () => 'black',
                        paddingLeft: () => 6,
                        paddingRight: () => 6,
                        paddingTop: () => 4,
                        paddingBottom: () => 4
                    },

                    margin: [0, 30, 0, 0]
                },

                // SUMMARY TABLE
                {
                    columns: [
                        { width: '*', text: '' },
                        {
                            width: 'auto',
                            table: {
                                widths: [113, 100],
                                body: [
                                    [{ text: `${this.translate.instant('pages.budget.pdf.SUBTOTAL')}: `, alignment: 'left', style: 'subheaderTable', border: [true, false, true, true] as [boolean, boolean, boolean, boolean] }, { text: formatMoney(documentData.subTotalAmount), style: 'priceCell', border: [true, false, true, true] as [boolean, boolean, boolean, boolean] }],
                                    [{ text: `${this.translate.instant('pages.budget.pdf.TAX', { TAX: documentData.VATPercentage ?? 0 })}: `, alignment: 'left', style: 'subheaderTable' }, { text: formatMoney(documentData.VATAmount), style: 'priceCell' }],
                                    [{ text: `${this.translate.instant('pages.budget.pdf.DISCOUNT')}: `, alignment: 'left', style: 'subheaderTable' }, { text: formatMoney(documentData.discount), style: 'priceCell' }],
                                    [{ text: `${this.translate.instant('pages.budget.pdf.total')}: `, bold: true, alignment: 'left', style: 'subheaderTable' }, { text: formatMoney(documentData.total), bold: true, style: 'priceCell' }]
                                ]
                            },
                            margin: [0, 0, 0, 0],
                            layout: {
                                hLineWidth: () => 1,
                                vLineWidth: () => 1,
                                hLineColor: () => 'black',
                                vLineColor: () => 'black',
                                paddingLeft: () => 6,
                                paddingRight: () => 6,
                                paddingTop: () => 4,
                                paddingBottom: () => 4
                            },

                            alignment: 'right'
                        }
                    ],

                    margin: [0, 8, 0, 0]
                },

                // EXTERNAL NOTES

                {
                    columns: [
                        // { width: '*', text: '' },
                        {
                            width: 'auto',
                            table: {
                                widths: ['*'],
                                body: [
                                    [{
                                        text: [...documentData?.budgetNotes]?.filter(x => x.type === 2)?.length ? `${this.translate.instant('pages.budget.pdf.NOTES')}` : '',
                                        alignment: 'left',
                                        style: 'subheaderTable',
                                        border: [false, false, false, false] as [boolean, boolean, boolean, boolean],
                                        margin: [0, 0, 0, 10] as [number, number, number, number]
                                    }],
                                    ...documentData.budgetNotes
                                        .filter(x => x.type === 2)
                                        .map((i) => [
                                            {
                                                text: i.content,
                                                style: 'tableCell',
                                                border: [false, false, false, false] as [boolean, boolean, boolean, boolean],
                                                margin: [0, 0, 0, 0] as [number, number, number, number]
                                            }
                                        ])

                                ]
                            },
                            margin: [0, 0, 0, 0],
                            layout: {
                                hLineWidth: () => 1,
                                vLineWidth: () => 1,
                                hLineColor: () => 'black',
                                vLineColor: () => 'black',
                                paddingLeft: () => 0,
                                paddingRight: () => 0,
                                paddingTop: () => 0,
                                paddingBottom: () => 0
                            },

                            alignment: 'right'
                        }
                    ],

                    margin: [0, 20, 0, 0]
                },



                // CLIENT SIGNATURE
                {
                    table: {
                        widths: ['*', 250],
                        body: [
                            [
                                { text: '', border: [false, false, false, false] as [boolean, boolean, boolean, boolean] },
                                {
                                    text: `${this.translate.instant('pages.budget.pdf.CLIENT_SIGNATURE')}`,
                                    color: 'black',
                                    margin: [0, 30, 0, 50] as [number, number, number, number],
                                    border: [false, false, false, false] as [boolean, boolean, boolean, boolean],
                                    alignment: 'left',
                                    fillColor: undefined
                                }
                            ]
                        ]
                    },

                    margin: [0, 30, 0, 0],
                    pageBreak: 'avoid' as any
                }
            ],
            footer: (currentPage: number, pageCount: number) => {
                return {
                    columns: [{
                        text: `${this.translate.instant('pages.budget.pdf.footer1')}
                        ${this.translate.instant('pages.budget.pdf.footer2')}`,
                        alignment: 'left',
                        fontSize: 8,
                        color: 'grey',
                        margin: [40, 5, 0, 0]
                    },
                    { text: this.translate.instant('pages.budget.pdf.pageCount', { current: currentPage, total: pageCount }), alignment: 'right', margin: [0, 20, 40, 0], fontSize: 9, width: 100, valign: 'middle' }
                    ]
                }
            },
            defaultStyle: {
                font: this.pdfFontLoader.fontName
            },
            styles: {
                header: { fontSize: 14, bold: true, color: 'black' },
                invoiceTitle: { fontSize: 24, bold: true, color: 'black' },
                tableHeader: { bold: true, fontSize: 10, fillColor: 'white', color: 'black', alignment: 'center', margin: [0, 0, 0, 0], lineHeight: 1.15 },
                dataText_blue: {
                    fontSize: 9, color: 'black'
                },
                simple_text_data: {
                    fontSize: 9, color: 'black', margin: [0, 0, 0, 2]
                },
                tableCell: {
                    fontSize: 9, color: 'black', margin: [0, 0, 0, 0], lineHeight: 1.2,
                },
                priceCell: { fontSize: 9, color: 'black', margin: [0, 0, 0, 0], lineHeight: 1.2 },
                subheaderTable: { fontSize: 10, color: 'black', margin: [0, 0, 0, 0], lineHeight: 1.2 },

            }
        };

        return docDefinition;
    }

    downloadPdfFromBase64(base64: string, fileName: string = 'document.pdf'): void {
        if (!base64) return;

        // Tolera tanto data URLs ("data:application/pdf;base64,...") como base64 puro.
        // Si el split no encuentra delimitador, devuelve el string original íntegro.
        const splitParts = base64.split('base64,');
        const cleanedBase64 = splitParts.length > 1 ? splitParts[1] : base64;

        let byteArray: Uint8Array;
        try {
            const byteCharacters = atob(cleanedBase64);
            byteArray = new Uint8Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteArray[i] = byteCharacters.charCodeAt(i);
            }
        } catch (error) {
            // base64 corrupta → no rompemos la app, mostramos error al usuario.
            console.error('Invalid base64 PDF payload:', error);
            return;
        }

        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
    }
}

